import { Box, VStack } from '@chakra-ui/react';
import { useReducedMotion } from 'framer-motion';
import { NavArrowDown } from 'iconoir-react';
import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import NavLink from '../nav-link';
import NavSubLink, { type SubNavItem } from './NavSubLink';

interface NavGroupProps {
  label: string;
  icon: React.ReactNode;
  items: SubNavItem[];
  /**
   * Word badge on the parent row, forwarded to NavLink, which already composes
   * it with the chevron in the same right-hand flex row.
   */
  tag?: string;
}

const matchesItem = (pathname: string, item: SubNavItem) =>
  item.exact ? pathname === item.to : pathname.startsWith(item.to);

/**
 * A top-level nav entry (Collabs, Affiliation) that expands an inline sub-menu.
 * Clicking the parent toggles the group — it does not navigate. The group opens
 * itself whenever one of its routes becomes active, and when collapsed with an
 * active child the parent shows the active pill; open, it only brightens its
 * label, since the child row already carries the pill.
 */
const NavGroup: React.FC<NavGroupProps> = ({ label, icon, items, tag }) => {
  const location = useLocation();
  const reduceMotion = useReducedMotion() ?? false;
  const isChildActive = items.some((item) => matchesItem(location.pathname, item));
  const [isOpen, setIsOpen] = useState(isChildActive);

  // Follow the route: auto-open when a child becomes active. Manual toggles
  // still win until the active child changes again.
  useEffect(() => {
    if (isChildActive) setIsOpen(true);
  }, [isChildActive]);

  const showActivePill = isChildActive && !isOpen;
  const brightenLabel = isChildActive && isOpen;

  return (
    <Box>
      <NavLink
        label={label}
        icon={icon}
        tag={tag}
        isActive={showActivePill}
        onClick={() => setIsOpen((open) => !open)}
        aria-expanded={isOpen}
        {...(brightenLabel ? { color: 'navDark.textStrong', fontWeight: 600 } : {})}
        rightChildren={
          <Box
            color="navDark.chevron"
            display="flex"
            flexShrink={0}
            style={{
              transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: reduceMotion ? 'none' : 'transform .18s cubic-bezier(.22,1,.36,1)',
            }}
          >
            <NavArrowDown width={16} height={16} />
          </Box>
        }
      />

      <Box
        style={{
          display: 'grid',
          gridTemplateRows: isOpen ? '1fr' : '0fr',
          transition: reduceMotion ? 'none' : 'grid-template-rows .3s cubic-bezier(.22,1,.36,1)',
        }}
      >
        <Box overflow="hidden" minH={0}>
          <Box
            mt="2px"
            mb="6px"
            ml="20px"
            pl="10px"
            borderLeft="1.5px solid"
            borderColor="navDark.subBorder"
          >
            <VStack gap="1px" align="stretch">
              {items.map((item, index) => (
                <NavSubLink
                  key={item.to}
                  {...item}
                  index={index}
                  isOpen={isOpen}
                  reduceMotion={reduceMotion}
                />
              ))}
            </VStack>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default NavGroup;
